/**
 * The chat composer on web, shared by the docked tray (`pill`) and the empty
 * state hero (`tall`).
 *
 * Desktop uses a two-row prompt box with a full-width input above the "+"
 * actions menu, model picker, dictation, and send or live voice action.
 * Narrow web keeps its compact layout. "+" offers the same per-message tools
 * as native; typing "@" filters sources inline and writes a mention;
 * typing "/" at the start of the draft lists the skills. All three share
 * `ComposerMenu`: one gliding highlight, arrows to move, Enter or Tab to pick,
 * Escape to dismiss until the next keystroke. On narrow web, wrapped text
 * moves onto its own row above the controls ("wide").
 *
 * Sites mode (plan 7.10): after Create on the Sites list, a removable
 * "Sites" chip sits at the start of the input and the placeholder reads
 * "Create a website that ...". The next message asks for a page that
 * becomes a draft site.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ComposerPrimitive, useAui, useAuiEvent, useAuiState } from '@assistant-ui/react';
import { usePathname } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Square } from 'lucide-react';
import { NovaListening } from '@/components/voice/NovaListening.web';
import { VoiceLevelMeter } from '@/components/voice/VoiceLevelMeter.web';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useToast } from '@/hooks/useToast';
import { joinTranscript } from '@/lib/voice/join-transcript';
import { MaterialSymbol } from '@/components/icons/MaterialSymbol';
import { HugeIcon, type HugeIconData } from '@/components/icons/HugeIcon';
import {
  Attachment01Icon,
  File01Icon,
  Globe02Icon,
  Image01Icon,
  LockIcon,
  Presentation01Icon,
  Tick02Icon,
  WebDesign01Icon,
} from '@hugeicons/core-free-icons';
import {
  addCapabilityChip,
  moveComposerMenuIndex,
  removeCapabilityChip,
  replaceComposerToken,
  withCapabilityChips,
  type CapabilityMentionKey,
} from '@/lib/agent-capabilities-mentions';
import { createChatUpgradeContext } from '@/lib/monetization-context';
import { useSubscriptionState } from '@/providers/useSubscriptionState';
import { clearSiteCompose, useSiteComposeMode } from '@/lib/site-compose';
import {
  artifactAskChipText,
  clearArtifactAsk,
  useArtifactAsk,
  useArtifactAskFocus,
} from '@/lib/artifact-ask';
import { useUpgradeModal } from '@/providers/UpgradeModalProvider';
import { ComposerAttachmentChips } from './composer-attachments.web';
import {
  ComposerMenu,
  composerMenuRowId,
  type ComposerMenuRow,
} from './composer-menu.web';
import {
  measureComposerMenuSpace,
  resolveComposerMenuPlacement,
  type ComposerMenuPlacement,
} from './composer-menu-placement';
import {
  filterComposerRows,
  useComposerSources,
  type ComposerSourceKey,
  type ComposerSourceRow,
} from './composer-sources';
import { resolveComposerMenu } from './composer-sources-model';
import { ComposerSweepCanvas } from './composer-sweep.web';
import { ModelPicker } from './ModelPicker.web';
import {
  SKILL_COMMAND_LIST_STYLE,
  useSkillCommandRows,
} from './SkillSlashMenu.web';

export type ComposerVariant = 'pill' | 'tall';

const SOURCE_ICONS: Record<ComposerSourceKey, HugeIconData> = {
  attach: Attachment01Icon,
  research: Globe02Icon,
  image: Image01Icon,
  result: File01Icon,
  document: File01Icon,
  slides: Presentation01Icon,
  page: WebDesign01Icon,
};

export const ComposerBar: FC<{
  variant: ComposerVariant;
  placeholder: string;
  autoFocus?: boolean;
  /** Phones carry the model picker in the chat header instead. */
  showModelPicker?: boolean;
  /** Match the native one-primary-action composer on phone web. */
  phoneControls?: boolean;
  /** Phones do not pull focus into the input (it raises the keyboard). */
  focusInputOnMenu?: boolean;
  employer: boolean;
  isSkillCommand: boolean;
  /** The plan's AI allowance is used up: Send opens the upgrade instead. */
  limitReached: boolean;
  limitLabel: string;
  onLimitReached: () => void;
  /** Above the attachments inside the composer, such as the page tag. */
  header?: ReactNode;
  /** Opens Voice live mode without replacing the capability controls. */
  liveModeButton?: ReactNode;
}> = ({
  variant,
  placeholder,
  autoFocus,
  showModelPicker = true,
  phoneControls = false,
  focusInputOnMenu = true,
  employer,
  isSkillCommand,
  limitReached,
  limitLabel,
  onLimitReached,
  header,
  liveModeButton,
}) => {
  const { t } = useTranslation('common');
  const aui = useAui();
  const text = useAuiState((s) => s.composer.text);
  const isEmpty = useAuiState((s) => s.composer.isEmpty);
  const hasAttachments = useAuiState((s) => s.composer.attachments.length > 0);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const { snapshot } = useSubscriptionState();
  const toast = useToast();
  const voice = useVoiceInput({
    role: employer ? 'employer' : 'seeker',
    surface: 'composer',
    onCapReached: () => toast.error(t('voice.cap.title'), t('voice.cap.message')),
    onTranscript: (transcript) => {
      aui.composer().setText(joinTranscript(aui.composer().getState().text, transcript));
    },
  });
  const isDictating = voice.status !== 'idle';
  const voiceSupported = typeof MediaRecorder !== 'undefined';
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();

  const menuId = `tabbio-composer-menu-${useId().replace(/:/g, '')}`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const attachRef = useRef<HTMLButtonElement | null>(null);

  const [plusOpen, setPlusOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [chips, setChips] = useState<CapabilityMentionKey[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(0);
  const [engaged, setEngaged] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [gridWidth, setGridWidth] = useState(0);
  const [focused, setFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const wide = variant === 'tall' || expanded;

  // The capability rows and "@" mentions apply to seeker threads that are
  // not slash commands; the server ignores them everywhere else.
  const capabilitiesEnabled = !employer && !isSkillCommand;
  const sources = useComposerSources({ capabilitiesEnabled });
  const sheetRows = useMemo(() => sources.rowsFor('sheet'), [sources]);
  const visibleChips = useMemo(() => {
    const offered = new Set(sheetRows.flatMap((row) =>
      row.action === 'mention' && row.mention ? [row.mention] : [],
    ));
    return chips.filter((chip) => offered.has(chip));
  }, [chips, sheetRows]);
  useAuiEvent('composer.send', () => setChips([]));
  const siteMode = useSiteComposeMode() && !employer;
  // The Sites chip sits at the start of the input's first line (the
  // reference's "Sites Create a website that ..."): the text starts after it.
  const siteChipRef = useRef<HTMLSpanElement | null>(null);
  const [siteChipWidth, setSiteChipWidth] = useState(0);
  useLayoutEffect(() => {
    setSiteChipWidth(siteMode && siteChipRef.current ? siteChipRef.current.offsetWidth + 6 : 0);
  }, [siteMode, t]);

  const { kind: menuKind, token } = resolveComposerMenu({
    text,
    plusOpen,
    dismissed,
    employer,
    capabilitiesEnabled,
  });
  const query = token?.query ?? '';

  const commands = useSkillCommandRows(
    token?.kind === 'command' ? token.query : undefined,
  );

  const sourceRows = useMemo<ComposerSourceRow[]>(() => {
    if (menuKind === 'sources') return sheetRows;
    if (menuKind === 'mentions') {
      return filterComposerRows(sources.rowsFor('mention'), query, sources.titleFor);
    }
    return [];
  }, [menuKind, query, sheetRows, sources]);

  const lockedTag = t('chat.model.locked.plus', { defaultValue: 'Plus' });
  const menuRows = useMemo<ComposerMenuRow[]>(() => {
    if (menuKind === 'commands') return commands.rows;
    return sourceRows.map((row) => ({
      key: row.key,
      leading: <HugeIcon icon={SOURCE_ICONS[row.key]} size={menuKind === 'sources' ? 24 : 18} strokeWidth={1.75} />,
      title: menuKind === 'sources'
        ? t(`chat.composer.sheet.rows.${row.key}.title`)
        : sources.titleFor(row),
      description: menuKind === 'sources'
        ? row.action === 'upgrade'
          ? t('chat.capabilities.image.lockedHint')
          : t(`chat.composer.sheet.rows.${row.key}.description`)
        : sources.descriptionFor(row),
      trailing:
        row.action === 'upgrade' ? (
          menuKind === 'sources'
            ? <HugeIcon icon={LockIcon} size={18} />
            : <span className="tabbio-composer-menu-lock"><HugeIcon icon={LockIcon} size={14} />{lockedTag}</span>
        ) : menuKind === 'sources' && row.mention && visibleChips.includes(row.mention) ? (
          <HugeIcon icon={Tick02Icon} size={20} strokeWidth={2.25} />
        ) : (
          null
        ),
      isSet: Boolean(menuKind === 'sources' && row.mention && visibleChips.includes(row.mention)),
    }));
  }, [commands.rows, lockedTag, menuKind, sourceRows, sources, t, visibleChips]);

  const menuVisible =
    menuKind !== null && !(menuKind === 'commands' && commands.loading);
  const activeIndex = Math.min(active, Math.max(0, menuRows.length - 1));

  // The menu opens above the box unless it would not fit there: on the
  // centred new-chat hero a tall menu flips below, or caps itself and
  // scrolls (composer-menu-placement.ts). Measured before paint.
  const [menuPlacement, setMenuPlacement] = useState<ComposerMenuPlacement>({
    placement: 'above',
    maxHeight: null,
  });
  // While a menu is open, a resized window or a growing box (the "@" menu
  // stays open as you type) changes the room on each side: measure again.
  const [menuLayoutTick, setMenuLayoutTick] = useState(0);
  useEffect(() => {
    const root = rootRef.current;
    if (!menuVisible || !root || typeof window === 'undefined') return;
    const remeasure = () => setMenuLayoutTick((tick) => tick + 1);
    window.addEventListener('resize', remeasure);
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(remeasure);
    observer?.observe(root);
    return () => {
      window.removeEventListener('resize', remeasure);
      observer?.disconnect();
    };
  }, [menuVisible]);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const menu = root?.querySelector<HTMLElement>('.tabbio-composer-menu');
    if (!menuVisible || !root || !menu) return;
    const next = resolveComposerMenuPlacement({
      ...measureComposerMenuSpace(root),
      // scrollHeight is the full list even under a cap; +2 for the border.
      menuHeight: menu.scrollHeight + 2,
    });
    setMenuPlacement((current) =>
      current.placement === next.placement && current.maxHeight === next.maxHeight
        ? current
        : next,
    );
  }, [menuVisible, menuKind, menuRows.length, menuLayoutTick]);

  // A new menu or a new query starts at the first row. A typed token shows
  // the highlight at once, since Enter picks it; the "+" menu waits for the
  // pointer or the arrows.
  const menuKey = `${menuKind ?? ''}:${menuKind === 'sources' ? '' : query}`;
  useEffect(() => {
    setActive(0);
    setEngaged(menuKind === 'mentions' || menuKind === 'commands');
  }, [menuKey, menuKind]);

  // Pointer-down anywhere outside the composer closes every menu.
  useEffect(() => {
    if (!menuVisible) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setPlusOpen(false);
      setDismissed(true);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuVisible]);

  const focusInput = useCallback(() => {
    if (focusInputOnMenu) inputRef.current?.focus({ preventScroll: true });
  }, [focusInputOnMenu]);

  // Ask Tabbio in the canvas (plan 7.4): the "Editing: ..." chip, and focus.
  const { chip: askChip } = useArtifactAsk();
  useArtifactAskFocus(() => inputRef.current?.focus({ preventScroll: true }));

  const openImageUpgrade = useCallback(() => {
    upgradeModal.open(
      createChatUpgradeContext({
        entryPoint: 'chat.capability_image_menu',
        planSet: snapshot?.planSet,
        recommendedTier: 'plus',
        returnTo: pathname,
      }),
    );
  }, [pathname, snapshot?.planSet, upgradeModal]);

  const sendWithChips = () => {
    if (isRunning || isDictating || isSkillCommand || (!text.trim() && !hasAttachments)) return;
    if (limitReached) {
      onLimitReached();
      return;
    }
    if (visibleChips.length) {
      aui.composer().setText(withCapabilityChips(
        text,
        visibleChips,
        (key) => t(`chat.composer.mentions.${key}`),
      ));
    }
    aui.composer().send();
    setChips([]);
  };

  const pick = (index: number) => {
    if (menuKind === 'commands') {
      commands.select(index);
      focusInput();
      return;
    }
    const row = sourceRows[index];
    if (!row) return;
    setPlusOpen(false);
    if (row.action === 'attach') {
      if (token) aui.composer().setText(replaceComposerToken(text, token, ''));
      // The picker must open inside this click or key press.
      attachRef.current?.click();
      return;
    }
    if (row.action === 'upgrade') {
      setDismissed(true);
      openImageUpgrade();
      return;
    }
    if (menuKind === 'sources' && row.mention) {
      setChips((current) => addCapabilityChip(current, row.mention!));
      focusInput();
      return;
    }
    aui
      .composer()
      .setText(replaceComposerToken(text, token, `@${sources.titleFor(row)} `));
    focusInput();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (isDictating && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      return;
    }
    if (menuVisible && menuRows.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setEngaged(true);
        setActive((current) =>
          moveComposerMenuIndex(
            Math.min(current, menuRows.length - 1),
            event.key === 'ArrowDown' ? 1 : -1,
            menuRows.length,
          ),
        );
        return;
      }
      if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
        // preventDefault also keeps the input primitive from sending.
        event.preventDefault();
        pick(activeIndex);
        return;
      }
    }
    if (event.key === 'Escape' && menuVisible) {
      event.preventDefault();
      setPlusOpen(false);
      setDismissed(true);
      return;
    }
    if (
      limitReached &&
      !isSkillCommand &&
      event.key === 'Enter' &&
      !event.shiftKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      onLimitReached();
      return;
    }
    if (visibleChips.length && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendWithChips();
    }
  };

  const shownPlaceholder = isDictating
    ? ''
    : siteMode
      ? t('sites.composer.placeholder')
      : placeholder;

  const rotatingPlaceholders = useMemo(
    () => employer
      ? [shownPlaceholder]
      : [
          shownPlaceholder,
          t('chat.composer.placeholderJobs'),
          t('chat.composer.placeholderCv'),
        ],
    [employer, shownPlaceholder, t],
  );
  useEffect(() => {
    if (!showModelPicker || focused || text || siteMode || isDictating || isSkillCommand || rotatingPlaceholders.length < 2) {
      setPlaceholderIndex(0);
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(
      () => setPlaceholderIndex((index) => (index + 1) % rotatingPlaceholders.length),
      3200,
    );
    return () => window.clearInterval(timer);
  }, [focused, isDictating, isSkillCommand, rotatingPlaceholders.length, showModelPicker, siteMode, text]);

  // Track the grid's width so wrapping is re-checked when the column resizes
  // (the artifact panel opening beside the chat, the window narrowing).
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setGridWidth(grid.clientWidth));
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  // Wide when the text has a newline or would not fit beside the controls,
  // measured with a hidden copy of the text rather than a character count.
  useLayoutEffect(() => {
    if (variant === 'tall') return;
    const grid = gridRef.current;
    const measure = measureRef.current;
    const input = inputRef.current;
    if (!grid || !measure || !input) return;
    const gridStyle = window.getComputedStyle(grid);
    const gap = parseFloat(gridStyle.columnGap) || 0;
    const controls = Array.from(
      grid.querySelectorAll<HTMLElement>('[data-composer-control]'),
    );
    const controlsWidth = controls.reduce(
      (sum, control) => sum + control.offsetWidth,
      0,
    );
    const inputStyle = window.getComputedStyle(input);
    const inputPadding =
      (parseFloat(inputStyle.paddingInlineStart) || 0) +
      (parseFloat(inputStyle.paddingInlineEnd) || 0);
    const inlineWidth = grid.clientWidth - controlsWidth - gap * controls.length;
    const next =
      text.includes('\n') ||
      measure.offsetWidth + inputPadding + siteChipWidth + 2 > inlineWidth;
    if (next !== expanded) setExpanded(next);
  }, [expanded, gridWidth, text, variant, showModelPicker, siteChipWidth, shownPlaceholder]);

  const hasInput = !isEmpty || hasAttachments;
  const shellClass = [
    'tabbio-composer-shell',
    variant === 'pill' ? 'is-pill' : 'is-tall',
    wide ? 'is-wide' : null,
    hasAttachments ? 'has-attachments' : null,
    !hasInput && !isRunning && liveModeButton ? 'is-empty' : null,
    isDictating ? 'is-dictating' : null,
    isDictating && isEmpty ? 'has-voice-status' : null,
  ]
    .filter(Boolean)
    .join(' ');
  const gridClass = [
    'tabbio-composer-grid',
    wide ? 'is-wide' : null,
    showModelPicker ? null : 'no-model',
    phoneControls ? 'phone-controls' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className="tabbio-composer" data-tabbio-composer="">
      {menuVisible && menuKind ? (
        <ComposerMenu
          id={menuId}
          label={
            menuKind === 'commands'
              ? t('skills.slashMenuTitle')
              : t('chat.capabilities.menuLabel')
          }
          rows={menuRows}
          activeIndex={activeIndex}
          showHighlight={engaged}
          onHover={(index) => {
            setActive(index);
            setEngaged(true);
          }}
          onLeave={() => {
            if (menuKind === 'sources') setEngaged(false);
          }}
          onPick={pick}
          compact={menuKind === 'sources'}
          footer={
            menuKind === 'commands'
              ? t('chat.composer.commandsFooter')
              : t('chat.composer.sourcesFooter')
          }
          emptyText={
            menuKind === 'commands'
              ? t('skills.slashMenuEmpty')
              : t('chat.composer.noMatches', { query })
          }
          listStyle={menuKind === 'commands' ? SKILL_COMMAND_LIST_STYLE : undefined}
          placement={menuPlacement.placement}
          maxHeight={menuPlacement.maxHeight}
        />
      ) : null}

      <div className={shellClass}>
        <ComposerSweepCanvas />
        {/* An empty input measures its placeholder, so a narrow column (the
            chat beside a file at 1024 px) goes wide instead of wrapping it. */}
        <span ref={measureRef} aria-hidden className="tabbio-composer-measure">
          {text || shownPlaceholder}
        </span>

        {header}
        {askChip && !employer ? (
          <div className="tabbio-composer-ask-row">
            <span className="tabbio-composer-ask-chip">
              <MaterialSymbol name="edit_note" size={16} />
              <span className="tabbio-composer-ask-chip-text" dir="auto">
                {t('chat.artifacts.selection.chip', { text: artifactAskChipText(askChip.label) })}
              </span>
              <button
                type="button"
                className="tabbio-composer-site-chip-remove"
                aria-label={t('chat.artifacts.selection.clear')}
                title={t('chat.artifacts.selection.clear')}
                onClick={() => {
                  clearArtifactAsk();
                  inputRef.current?.focus({ preventScroll: true });
                }}
              >
                <MaterialSymbol name="close" size={14} />
              </button>
            </span>
          </div>
        ) : null}
        {!employer && !isSkillCommand && visibleChips.length > 0 ? (
          <div className="tabbio-composer-active-tools" aria-label={t('chat.composer.chips.label')}>
            {visibleChips.map((chip) => (
              <span key={chip} className="tabbio-composer-active-tool">
                <HugeIcon icon={SOURCE_ICONS[chip]} size={16} strokeWidth={1.75} />
                {t(`chat.composer.mentions.${chip}`)}
                <button type="button"
                  aria-label={t('chat.composer.chips.remove', { name: t(`chat.composer.mentions.${chip}`) })}
                  onClick={() => setChips((current) => removeCapabilityChip(current, chip))}>
                  <MaterialSymbol name="close" size={14} />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <ComposerAttachmentChips />

        <div ref={gridRef} className={gridClass}>
          {/* The menu's upload action opens this assistant-ui file picker. */}
          <ComposerPrimitive.AddAttachment asChild>
            <button
              ref={attachRef}
              type="button"
              className="tabbio-composer-attach-trigger"
              tabIndex={-1}
              aria-hidden
              disabled={isRunning || isDictating}
            />
          </ComposerPrimitive.AddAttachment>
          <button
            type="button"
            data-composer-control=""
            className={
              plusOpen
                ? 'tabbio-composer-btn tabbio-composer-plus is-open'
                : 'tabbio-composer-btn tabbio-composer-plus'
            }
            aria-haspopup="listbox"
            aria-expanded={plusOpen}
            aria-controls={plusOpen ? menuId : undefined}
            disabled={isRunning || voice.status === 'transcribing'}
            aria-label={voice.status === 'listening' ? t('actions.cancel') : t('chat.capabilities.menuLabel')}
            title={voice.status === 'listening' ? t('actions.cancel') : t('chat.capabilities.menuLabel')}
            onClick={() => {
              if (voice.status === 'listening') {
                void voice.cancel();
                return;
              }
              setModelOpen(false);
              setPlusOpen((open) => !open);
              setDismissed(false);
              focusInput();
            }}
          >
            <MaterialSymbol name={voice.status === 'listening' ? 'close' : 'add'} size={20} />
          </button>

          {siteMode ? (
            <span ref={siteChipRef} className="tabbio-composer-site-chip">
              <MaterialSymbol name="public" size={16} />
              <span>{t('sites.composer.chip')}</span>
              <button
                type="button"
                className="tabbio-composer-site-chip-remove"
                aria-label={t('sites.composer.removeChip')}
                title={t('sites.composer.removeChip')}
                onClick={() => {
                  clearSiteCompose();
                  focusInput();
                }}
              >
                <MaterialSymbol name="close" size={14} />
              </button>
            </span>
          ) : null}

          {showModelPicker && !focused && !text && !siteMode && !isDictating ? (
            <span key={placeholderIndex} className="tabbio-composer-rotating-placeholder" aria-hidden>
              {rotatingPlaceholders[placeholderIndex]}
            </span>
          ) : null}

          <ComposerPrimitive.Input
            ref={inputRef}
            rows={1}
            autoFocus={autoFocus}
            placeholder={showModelPicker && !siteMode ? '' : shownPlaceholder}
            aria-label={shownPlaceholder || placeholder}
            className="tabbio-composer-input"
            style={siteChipWidth ? { textIndent: siteChipWidth } : undefined}
            cancelOnEscape={!menuVisible}
            role="combobox"
            aria-expanded={menuVisible}
            aria-controls={menuVisible ? menuId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              menuVisible && engaged && menuRows.length > 0
                ? composerMenuRowId(menuId, activeIndex)
                : undefined
            }
            onChange={() => {
              setDismissed(false);
              setPlusOpen(false);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={handleKeyDown}
          />

          {isDictating && isEmpty ? (
            <div className="tabbio-composer-voice-status" role="status">
              <NovaListening state={voice.status === 'listening' ? 'listening' : 'transcribing'} level={voice.level} hideMeter />
            </div>
          ) : null}

          {showModelPicker ? (
            <div data-composer-control="" className="tabbio-composer-model-slot">
              <ModelPicker open={modelOpen} onOpenChange={(open) => {
                setModelOpen(open);
                if (open) {
                  setPlusOpen(false);
                  setDismissed(true);
                }
              }} />
            </div>
          ) : null}

          {phoneControls ? (
            <>
              {!isRunning && !hasInput && voice.status === 'idle' && liveModeButton ? (
                <div data-composer-control="" className="tabbio-composer-live-slot">{liveModeButton}</div>
              ) : null}
              <div data-composer-control="" className="tabbio-phone-primary-control">
                {isRunning ? (
                  <ComposerPrimitive.Cancel asChild>
                    <button type="button" className="tabbio-composer-send is-stop"
                      aria-label={t('chat.composer.stop')} title={t('chat.composer.stop')}>
                      <Square size={14} className="fill-current" aria-hidden />
                    </button>
                  </ComposerPrimitive.Cancel>
                ) : voice.status === 'listening' ? (
                  <>
                    <VoiceLevelMeter level={voice.level} className="tabbio-composer-dictation-meter" />
                    <button type="button" className="tabbio-composer-send is-listening"
                      aria-label={t('chat.dictationStop')} title={t('chat.dictationStop')}
                      onClick={voice.toggle}>
                      <Square size={14} className="fill-current" aria-hidden />
                    </button>
                  </>
                ) : voice.status === 'transcribing' ? (
                  <button type="button" className="tabbio-composer-send" disabled
                    aria-label={t('chat.composer.dictate')}>
                    <MaterialSymbol name="mic" size={20} />
                  </button>
                ) : hasInput ? (
                  limitReached && !isSkillCommand ? (
                    <button type="button" className="tabbio-composer-send" onClick={onLimitReached}
                      aria-label={limitLabel} title={limitLabel}>
                      <MaterialSymbol name="arrow_upward" size={20} />
                    </button>
                  ) : visibleChips.length > 0 && !isSkillCommand ? (
                    <button type="button" className="tabbio-composer-send" onClick={sendWithChips}
                      aria-label={t('chat.composer.send')} title={t('chat.composer.send')}>
                      <MaterialSymbol name="arrow_upward" size={20} />
                    </button>
                  ) : (
                    <ComposerPrimitive.Send asChild>
                      <button type="button" className="tabbio-composer-send"
                        aria-label={t('chat.composer.send')} title={t('chat.composer.send')}>
                        <MaterialSymbol name="arrow_upward" size={20} />
                      </button>
                    </ComposerPrimitive.Send>
                  )
                ) : (
                  <button type="button" className="tabbio-composer-send" onClick={voice.toggle}
                    disabled={!voiceSupported} aria-label={t('chat.composer.dictate')}
                    title={t('chat.composer.dictate')}>
                    <MaterialSymbol name="mic" size={20} />
                  </button>
                )}
              </div>
            </>
          ) : (
          <>
          <div data-composer-control="" className="tabbio-composer-live-slot">{liveModeButton}</div>

          <div data-composer-control="" className="tabbio-composer-dictation-control">
            {voice.status === 'listening' ? (
              <VoiceLevelMeter level={voice.level} className="tabbio-composer-dictation-meter" />
            ) : null}
            <button
              type="button"
              className={`tabbio-composer-btn tabbio-composer-mic${voice.status === 'listening' ? ' is-listening' : ''}`}
              aria-pressed={voice.status === 'listening'}
              aria-label={voice.status === 'listening' ? t('chat.dictationStop') : t('chat.composer.dictate')}
              title={voice.status === 'listening' ? t('chat.dictationStop') : t('chat.composer.dictate')}
              disabled={!voiceSupported || voice.status === 'transcribing' || isRunning}
              onClick={voice.toggle}
            >
              {voice.status === 'listening'
                ? <Square size={14} fill="currentColor" strokeWidth={2} aria-hidden />
                : <MaterialSymbol name="mic" size={20} />}
            </button>
          </div>

          {isRunning ? (
            <ComposerPrimitive.Cancel asChild>
              <button
                type="button"
                data-composer-control=""
                className="tabbio-composer-send is-stop"
                aria-label={t('chat.composer.stop')}
                title={t('chat.composer.stop')}
              >
                <Square size={13} className="fill-current" />
              </button>
            </ComposerPrimitive.Cancel>
          ) : limitReached && !isSkillCommand ? (
            <button
              type="button"
              data-composer-control=""
              className="tabbio-composer-send"
              disabled={!hasInput || isDictating}
              onClick={onLimitReached}
              aria-label={limitLabel}
              title={limitLabel}
            >
              <MaterialSymbol name="arrow_upward" size={20} />
            </button>
          ) : visibleChips.length > 0 && !isSkillCommand ? (
            <button type="button" data-composer-control="" className="tabbio-composer-send"
              disabled={!hasInput || isDictating} onClick={sendWithChips}
              aria-label={t('chat.composer.send')} title={t('chat.composer.send')}>
              <MaterialSymbol name="arrow_upward" size={20} />
            </button>
          ) : (
            <ComposerPrimitive.Send asChild>
              <button
                type="button"
                data-composer-control=""
                className="tabbio-composer-send"
                disabled={isDictating}
                aria-label={t('chat.composer.send')}
                title={t('chat.composer.send')}
              >
                <MaterialSymbol name="arrow_upward" size={20} />
              </button>
            </ComposerPrimitive.Send>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};
